/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * This file is part of Mythbound Adventure Studio, a proprietary software project.
 * Unauthorized copying, modification, distribution, or use of this software,
 * via any medium, is strictly prohibited without explicit written permission
 * from the copyright holders.
 *
 * For licensing inquiries, contact: taleweavertik@gmail.com
 */

/**
 * index.tsx
 *
 * This is the entry point for the Mythbound™ web application.
 * It renders the root App component to the DOM and sets up global providers.
 *
 * Note: OAuth credentials are now managed by backend API calls rather than
 * environment variables, providing better security and cross-platform compatibility.
 *
 * Last Modified: 2026-02-26
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { ChakraProvider } from '@chakra-ui/react';
import { theme } from './config/theme';

// Note: Legacy credential management removed
// OAuth credentials now loaded via backend API using getBackendConfig() and getDiscordConfig()
// This eliminates the need for frontend environment variables

// Add diagnostic data type to window
declare global {
  interface Window {
    mythboundDiagnostics?: {
      logs: Array<{
        timestamp: string;
        message: string;
        data?: string;
      }>
    };
  }
}

// Create root element and render the App
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ChakraProvider
      theme={theme}
      // Added 2026-02-26: global toast size/position config — reduces oversized default Chakra toasts
      toastOptions={{
        defaultOptions: {
          duration: 4000,
          isClosable: true,
          position: 'top-right',
          containerStyle: {
            maxWidth: '320px',
            fontSize: '0.875rem',
          },
        },
      }}
    >
      <App />
    </ChakraProvider>
  </React.StrictMode>
);